import React from 'react';
import {
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Calendar
} from 'lucide-react';
import { PreventiveMeasure, FallPreventionProgress } from '../types';

interface PreventionTabProps {
  preventiveMeasures: PreventiveMeasure[];
  progress: FallPreventionProgress | null;
}

const PreventionTab: React.FC<PreventionTabProps> = ({
  preventiveMeasures,
  progress
}) => {
  // Group measures by status
  const groupedMeasures = preventiveMeasures.reduce(
    (acc, measure) => {
      acc[measure.status].push(measure);
      return acc;
    },
    {
      IMPLEMENTED: [] as PreventiveMeasure[],
      IN_PROGRESS: [] as PreventiveMeasure[],
      PLANNED: [] as PreventiveMeasure[]
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IMPLEMENTED':
        return 'text-green-500';
      case 'IN_PROGRESS':
        return 'text-yellow-500';
      case 'PLANNED':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IMPLEMENTED':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'IN_PROGRESS':
        return <Clock className="text-yellow-500" size={20} />;
      case 'PLANNED':
        return <Calendar className="text-blue-500" size={20} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      {progress && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Safety Measures</h3>
              <Shield className="text-blue-500" size={24} />
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                    Progress
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-blue-600">
                    {progress.safetyMeasuresProgress}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                <div
                  style={{ width: `${progress.safetyMeasuresProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Risk Assessment</h3>
              <AlertTriangle className="text-purple-500" size={24} />
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-purple-600 bg-purple-200">
                    Coverage
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-purple-600">
                    {progress.riskAssessmentProgress}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-purple-200">
                <div
                  style={{ width: `${progress.riskAssessmentProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Family Education</h3>
              <CheckCircle className="text-green-500" size={24} />
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-green-600 bg-green-200">
                    Completion
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-green-600">
                    {progress.staffTrainingProgress}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-green-200">
                <div
                  style={{ width: `${progress.staffTrainingProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preventive Measures */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Implemented Measures */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Implemented</h3>
            <CheckCircle className="text-green-500" size={24} />
          </div>
          <div className="space-y-4">
            {groupedMeasures.IMPLEMENTED.map((measure) => (
              <div
                key={measure.id}
                className="p-4 bg-green-50 rounded-lg border border-green-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-green-900">
                      {measure.title}
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      {measure.description}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-green-600">
                      <Calendar className="mr-1" size={14} />
                      {measure.implementationDate ? new Date(measure.implementationDate).toLocaleDateString() : 'Not set'}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {measure.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* In Progress Measures */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">In Progress</h3>
            <Clock className="text-yellow-500" size={24} />
          </div>
          <div className="space-y-4">
            {groupedMeasures.IN_PROGRESS.map((measure) => (
              <div
                key={measure.id}
                className="p-4 bg-yellow-50 rounded-lg border border-yellow-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-yellow-900">
                      {measure.title}
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {measure.description}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-yellow-600">
                      <Calendar className="mr-1" size={14} />
                      Target: {measure.targetDate ? new Date(measure.targetDate).toLocaleDateString() : 'Not set'}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {measure.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Planned Measures */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Planned</h3>
            <Calendar className="text-blue-500" size={24} />
          </div>
          <div className="space-y-4">
            {groupedMeasures.PLANNED.map((measure) => (
              <div
                key={measure.id}
                className="p-4 bg-blue-50 rounded-lg border border-blue-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-blue-900">
                      {measure.title}
                    </h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {measure.description}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-blue-600">
                      <Calendar className="mr-1" size={14} />
                      Planned: {measure.targetDate ? new Date(measure.targetDate).toLocaleDateString() : 'Not set'}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {measure.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreventionTab; 